(() => {
  const moneyFormat = window?.Shopify?.money_format || '{{amount_with_comma_separator}}€';

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    const value = (Number(cents || 0) / 100).toFixed(2).replace('.', ',');
    return value.endsWith(',00') ? value.replace(',00', '') + '€' : value + '€';
  }

  function getSelectedOptions(variantSelects) {
    const fieldsets = Array.from(variantSelects.querySelectorAll('fieldset'));
    const options = [];

    fieldsets.forEach((fieldset) => {
      const select = fieldset.querySelector('select');
      const checked = fieldset.querySelector('input:checked');

      if (select) {
        options.push(select.value);
      } else if (checked) {
        options.push(checked.value);
      }
    });

    return options;
  }

  function getVariants(variantSelects) {
    const jsonNode = variantSelects.querySelector('script[type="application/json"]');
    if (!jsonNode) return [];
    try {
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      return [];
    }
  }

  function getVariantMeta(root) {
    const jsonNode = root.querySelector('[data-quantity-variant-meta]');
    if (!jsonNode) return {};
    try {
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      return {};
    }
  }

  function findVariantForCard(value, optionPosition, selectedOptions, variants) {
    return variants.find((variant) => {
      return variant.options.every((optionValue, index) => {
        if (index === optionPosition - 1) return optionValue === value;
        return optionValue === selectedOptions[index];
      });
    });
  }

  function computeDerivedComparePrice(card, variant) {
    if (!variant) return 0;

    const quantityMultiplier = Number(card.dataset.quantityMultiplier || 1);
    const singleBasePrice = Number(card.dataset.singleBasePrice || 0);
    const nativeCompareAt = Number(variant.compare_at_price || 0);

    if (quantityMultiplier > 1 && singleBasePrice > 0) {
      const derived = singleBasePrice * quantityMultiplier;
      if (derived > variant.price) return derived;
    }

    if (nativeCompareAt > variant.price) return nativeCompareAt;

    return 0;
  }

  function getDefaultCardText(card) {
    if (card.dataset.singleLabel === 'true') {
      return {
        title: '1 unité',
        subtitle: 'Parfait pour un seul côté',
        badge: '',
      };
    }

    if (card.dataset.pairLabel === 'true') {
      return {
        title: '2 unités',
        subtitle: "La paire, l'harmonie des deux côtés",
        badge: '',
      };
    }

    if (card.dataset.trioLabel === 'true') {
      return {
        title: '3 unités',
        subtitle: 'Le trio, encore plus avantageux',
        badge: '',
      };
    }

    return {
      title: card.dataset.optionValue || '',
      subtitle: '',
      badge: '',
    };
  }

  function updateBadge(card, meta, fallbackBadge) {
    const badgeNode = card.querySelector('.eut-quantity-card__badge');
    if (!badgeNode) return;

    badgeNode.textContent = meta.cardBadge || fallbackBadge || '';
    badgeNode.style.backgroundColor = meta.cardBadgeColor || '';
    badgeNode.style.color = meta.cardBadgeTextColor || '';
  }

  function updateCard(card, variant, isActive, meta = {}) {
    const priceNode = card.querySelector('.eut-quantity-card__price');
    const compareNode = card.querySelector('.eut-quantity-card__compare');
    const saveNode = card.querySelector('.eut-quantity-card__save');
    const soldoutNode = card.querySelector('.eut-quantity-card__soldout');
    const imageNode = card.querySelector('.eut-quantity-card__image');
    const titleNode = card.querySelector('.eut-quantity-card__title');
    const subtitleNode = card.querySelector('.eut-quantity-card__subtitle');
    const defaults = getDefaultCardText(card);

    card.classList.toggle('is-active', isActive);
    card.setAttribute('aria-pressed', isActive ? 'true' : 'false');

    if (!variant) {
      card.classList.add('is-unavailable');
      if (titleNode) titleNode.textContent = defaults.title;
      if (subtitleNode) subtitleNode.textContent = defaults.subtitle;
      updateBadge(card, {}, defaults.badge);
      if (priceNode) priceNode.textContent = '';
      if (compareNode) compareNode.textContent = '';
      if (saveNode) saveNode.textContent = '';
      if (soldoutNode) soldoutNode.textContent = 'Indisponible';
      return;
    }

    const compareAtPrice = computeDerivedComparePrice(card, variant);
    const saveAmount = compareAtPrice > variant.price ? compareAtPrice - variant.price : 0;

    card.dataset.variantId = variant.id;
    card.dataset.price = variant.price || 0;
    card.dataset.compareAtPrice = variant.compare_at_price || 0;
    card.dataset.derivedCompareAtPrice = compareAtPrice || 0;
    card.dataset.available = String(variant.available);
    card.dataset.highlight = meta.cardHighlight ? 'true' : 'false';

    card.classList.toggle('is-unavailable', !variant.available);

    if (titleNode) titleNode.textContent = meta.cardTitle || defaults.title;
    if (subtitleNode) subtitleNode.textContent = meta.cardSubtitle || defaults.subtitle;
    updateBadge(card, meta, defaults.badge);

    if (priceNode) priceNode.textContent = formatMoney(variant.price);
    if (compareNode) {
      compareNode.textContent = compareAtPrice > variant.price ? formatMoney(compareAtPrice) : '';
    }

    if (saveNode) {
      saveNode.textContent = saveAmount > 0 ? `🔥 Economisez ${formatMoney(saveAmount)}` : '';
    }

    if (soldoutNode) {
      soldoutNode.textContent = variant.available ? '' : 'Indisponible';
    }

    if (imageNode) {
      const nextImage = meta.cardImage || variant.featured_image?.src || card.dataset.fallbackImageSrc || '';
      if (nextImage) {
        imageNode.src = nextImage;
        imageNode.removeAttribute('srcset');
      }
      if (variant.featured_image?.alt) imageNode.alt = variant.featured_image.alt;
    }
  }

  function initQuantityPicker(root) {
    if (!root || root.dataset.quantityCardsReady === 'true') return;

    const variantSelects = root.closest('variant-selects');
    if (!variantSelects) return;

    const nativeFieldset = root.querySelector('.eut-quantity-picker__native fieldset');
    if (!nativeFieldset) return;

    const variants = getVariants(variantSelects);
    const variantMeta = getVariantMeta(root);
    const optionPosition = Number(root.dataset.optionPosition || 0);
    const valueLabel = root.querySelector('.eut-quantity-picker__value');
    const cards = Array.from(root.querySelectorAll('[data-quantity-card]'));

    if (!variants.length || !cards.length || !optionPosition) return;

    function syncCards() {
      const selectedOptions = getSelectedOptions(variantSelects);
      const checkedInput = nativeFieldset.querySelector('input:checked');
      const singleCard = cards.find((card) => card.dataset.singleLabel === 'true');
      const singleVariant = singleCard
        ? findVariantForCard(singleCard.dataset.optionValue, optionPosition, selectedOptions, variants)
        : null;

      cards.forEach((card) => {
        const value = card.dataset.optionValue;
        const variant = findVariantForCard(value, optionPosition, selectedOptions, variants);
        const isActive = checkedInput && checkedInput.value === value;
        if (singleVariant) card.dataset.singleBasePrice = singleVariant.price || 0;
        if (!variant) {
          card.style.display = 'none';
        } else {
          card.style.display = '';
          updateCard(card, variant, isActive, variantMeta[String(variant.id)] || {});
        }
      });

      if (valueLabel && checkedInput) {
        valueLabel.textContent = checkedInput.value;
      }
    }

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const value = card.dataset.optionValue;
        const input = nativeFieldset.querySelector(`input[value="${CSS.escape(value)}"]`);
        if (!input || input.checked) return;

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        syncCards();

        // Dispatch custom event for loyalty points
        const variant = findVariantForCard(value, optionPosition, getSelectedOptions(variantSelects), variants);
        if (variant) {
          window.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant } }));
        }
      });
    });

    variantSelects.addEventListener('change', () => {
      window.requestAnimationFrame(syncCards);
    });

    root.dataset.quantityCardsReady = 'true';
    syncCards();
  }

  function initAllQuantityPickers(scope = document) {
    scope.querySelectorAll('.eut-quantity-picker').forEach(initQuantityPicker);
  }

  document.addEventListener('DOMContentLoaded', () => initAllQuantityPickers());

  document.addEventListener('shopify:section:load', (event) => {
    initAllQuantityPickers(event.target);
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('.eut-quantity-picker')) {
          initQuantityPicker(node);
        } else if (node.querySelectorAll) {
          initAllQuantityPickers(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
